import React, { useEffect, useState } from 'react';
import { BadgeCheck, MessageSquareText, Star } from 'lucide-react';
import { getProductReviews } from '@/api/shop';
import { Stars } from '@/components/shop/ShopUI';
import { Shimmer } from '@/components/ui';
import { istDate } from '@/lib/shop';

const SORTS = [
  { value: '', label: 'Most recent' },
  { value: 'rating_desc', label: 'Highest rated' },
  { value: 'rating_asc', label: 'Lowest rated' },
];

/** Ratings breakdown bars + the review list with star and sort filters. */
const ProductReviews = ({ slug, rating }) => {
  const [filter, setFilter] = useState({ rating: '', sort: '' });
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPage(1);
  }, [slug, filter]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const params = { page };
    if (filter.rating) params.rating = filter.rating;
    if (filter.sort) params.sort = filter.sort;
    getProductReviews(slug, params)
      .then((res) => {
        if (!live) return;
        setList((prev) => (page === 1 ? res.data || [] : [...prev, ...(res.data || [])]));
        setMeta(res.pagination || null);
      })
      .catch(() => live && page === 1 && setList([]))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [slug, filter, page]);

  const count = rating?.count || 0;
  const breakdown = rating?.breakdown || {};
  const max = Math.max(1, ...Object.values(breakdown).map(Number));

  return (
    <section id="reviews" className="lk-card p-5 sm:p-6">
      <h2 className="text-h3 font-semibold text-ink-900">Ratings &amp; reviews</h2>

      {count === 0 ? (
        <div className="mt-4 flex items-center gap-3 rounded-2xl bg-surface-muted p-4 text-sm text-ink-500">
          <MessageSquareText className="h-5 w-5 shrink-0" />
          No reviews yet. Buyers can review this product after it is delivered.
        </div>
      ) : (
        <div className="mt-4 grid gap-6 md:grid-cols-[220px_1fr]">
          <div>
            <div className="flex items-end gap-2">
              <span className="tabular text-5xl font-bold leading-none text-ink-900">{Number(rating.average).toFixed(1)}</span>
              <Star className="mb-1 h-7 w-7 fill-amber-400 text-amber-400" />
            </div>
            <p className="mt-1 text-sm text-ink-500">
              {count.toLocaleString('en-IN')} rating{count === 1 ? '' : 's'}
            </p>
            <div className="mt-4 space-y-1.5">
              {[5, 4, 3, 2, 1].map((n) => {
                const c = Number(breakdown[n] || 0);
                const tone = n >= 3 ? 'bg-success' : n === 2 ? 'bg-warning' : 'bg-danger';
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setFilter((f) => ({ ...f, rating: f.rating === String(n) ? '' : String(n) }))}
                    className={`lk-focus flex w-full items-center gap-2 rounded px-1 text-xs ${filter.rating === String(n) ? 'bg-brand-tint' : ''}`}
                    aria-label={`${n} star: ${c}`}
                  >
                    <span className="tabular w-6 shrink-0 text-ink-700">{n}★</span>
                    <span className="h-2 flex-1 overflow-hidden rounded-full bg-surface-sunken">
                      <span className={`block h-full rounded-full ${tone}`} style={{ width: `${(c / max) * 100}%` }} />
                    </span>
                    <span className="tabular w-6 shrink-0 text-right text-ink-500">{c}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {['', '5', '4', '3', '2', '1'].map((r) => (
                <button
                  key={r || 'all'}
                  type="button"
                  onClick={() => setFilter((f) => ({ ...f, rating: r }))}
                  className={`lk-chip min-h-[36px] px-3 py-1 text-xs ${filter.rating === r ? 'lk-chip-active' : 'lk-chip-idle'}`}
                >
                  {r ? `${r}★` : 'All'}
                </button>
              ))}
              <select
                value={filter.sort}
                onChange={(e) => setFilter((f) => ({ ...f, sort: e.target.value }))}
                aria-label="Sort reviews"
                className="lk-focus ml-auto rounded-xl border border-ink-200 bg-white py-2 pl-3 pr-8 text-xs font-semibold text-ink-900"
              >
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <ul className="mt-4 divide-y divide-ink-200/70">
              {loading && page === 1
                ? Array.from({ length: 2 }, (_, i) => (
                    <li key={i} className="space-y-2 py-4">
                      <Shimmer className="h-4 w-32" />
                      <Shimmer className="h-4 w-full" />
                    </li>
                  ))
                : list.map((r) => (
                    <li key={r.id} className="py-4">
                      <div className="flex items-center gap-2">
                        <Stars value={r.rating} size="h-3.5 w-3.5" />
                        {r.title && <span className="text-sm font-semibold text-ink-900">{r.title}</span>}
                      </div>
                      {r.body && <p className="mt-1.5 whitespace-pre-line text-sm text-ink-700">{r.body}</p>}
                      {r.images?.length > 0 && (
                        <div className="mt-2 flex gap-2">
                          {r.images.map((src) => (
                            <img key={src} src={src} alt="" className="h-16 w-16 rounded-lg object-cover ring-1 ring-ink-200" />
                          ))}
                        </div>
                      )}
                      <p className="mt-2 flex flex-wrap items-center gap-x-2 text-xs text-ink-500">
                        <span className="font-medium text-ink-700">{r.author}</span>
                        {r.variantLabel && <span>· {r.variantLabel}</span>}
                        {r.verifiedPurchase && (
                          <span className="inline-flex items-center gap-0.5 text-success">
                            <BadgeCheck className="h-3.5 w-3.5" /> Verified buyer
                          </span>
                        )}
                        <span>· {istDate(r.createdAt)}</span>
                      </p>
                    </li>
                  ))}
              {!loading && list.length === 0 && <li className="py-6 text-center text-sm text-ink-500">No reviews match this filter.</li>}
            </ul>
            {meta && meta.currentPage < meta.lastPage && (
              <button type="button" disabled={loading} onClick={() => setPage((p) => p + 1)} className="lk-btn-ghost mt-2 w-full">
                {loading ? 'Loading…' : 'Show more reviews'}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default ProductReviews;
